import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { downloadLegacyFile, readLegacyFile } from '@/pages/legacy/shared';
import { DOWNLOAD_PROFILE_SCAN, UPLOAD_PROFILE_SCAN } from './queries';

interface ScanFile { filename: string; content: string; contentType: string }
const fileTypes = [{ value: 'courtFile', label: 'Court file for case' }, { value: 'rapSheet', label: 'Rapsheet' }, { value: 'otherFile', label: 'Other file' }];

export const ProfileScanUpload = ({ odrnumber, onSaved, onClose }: {
  odrnumber: number; onSaved: (warnings: string[]) => Promise<unknown>; onClose: () => void;
}) => {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const { register, control, handleSubmit, formState: { errors } } = useForm<{ type: string; label: string; files: FileList }>({ defaultValues: { type: 'courtFile', label: '' } });
  const [upload] = useMutation<{ uploadParticipantProfileScan: { success: boolean; warnings?: string[] | null } | null }>(UPLOAD_PROFILE_SCAN);
  const submit = handleSubmit(async values => {
    const file = values.files[0];
    if (!file) return;
    setPending(true); setError(null);
    try {
      const content = await readLegacyFile(file);
      const result = await upload({ variables: { odrnumber, type: values.type, label: values.label, filename: file.name, content } });
      if (result.data?.uploadParticipantProfileScan?.success !== true) throw new Error('Unable to upload this scan.');
      await onSaved(result.data.uploadParticipantProfileScan.warnings ?? []);
      onClose();
    } catch (error) { setError(error instanceof Error ? error.message : 'Unable to upload this scan.'); }
    finally { setPending(false); }
  });
  return <form id="profile-scan-upload-form" className="space-y-4" onSubmit={submit}>
    {error && <p role="alert">{error}</p>}
    <div><Label htmlFor="profile-scan-upload-type">File type</Label><Controller name="type" control={control} render={({ field }) => <Select value={field.value} onValueChange={field.onChange}><SelectTrigger id="profile-scan-upload-type" className="h-8"><SelectValue /></SelectTrigger><SelectContent>{fileTypes.map(type => <SelectItem id={`profile-scan-upload-type-${type.value}`} key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent></Select>} /></div>
    <div><Label htmlFor="profile-scan-upload-label">Case number or file label</Label><Input id="profile-scan-upload-label" className="h-8" maxLength={60} {...register('label')} /></div>
    <div><Label htmlFor="profile-scan-upload-file">File</Label><Input id="profile-scan-upload-file" className="h-8" type="file" {...register('files', { validate: files => Boolean(files?.[0]) && files[0].size <= 32 * 1024 * 1024 })} /></div>
    {errors.files && <p role="alert">Choose a file of 32 MB or less.</p>}
    <div className="flex justify-end gap-2"><Button id="profile-scan-upload-save" type="submit" disabled={pending}><Upload aria-hidden="true" className="mr-2 h-4 w-4" />Upload</Button><Button id="profile-scan-upload-cancel" type="button" variant="outline" disabled={pending} onClick={onClose}>Cancel</Button></div>
  </form>;
};

export const ProfileScanDownload = ({ odrnumber, recordKey }: { odrnumber: number; recordKey: string }) => {
  const [error, setError] = useState<string | null>(null);
  const [download, state] = useLazyQuery<{ downloadParticipantProfileScan: ScanFile | null }>(DOWNLOAD_PROFILE_SCAN, { fetchPolicy: 'network-only' });
  const open = async () => {
    setError(null);
    const preview = window.open('about:blank', '_blank');
    if (!preview) { setError('Unable to open this scan.'); return; }
    preview.opener = null;
    try {
      const result = await download({ variables: { odrnumber, key: recordKey } });
      if (result.error) throw result.error;
      const file = result.data?.downloadParticipantProfileScan;
      if (!file) throw new Error('Unable to download this scan.');
      downloadLegacyFile(file, preview);
    } catch (error) { preview.close(); setError(error instanceof Error ? error.message : 'Unable to download this scan.'); }
  };
  return <div>{error && <p role="alert">{error}</p>}<Button id={`profile-scan-${recordKey.replace(/\W/g, '_')}-download`} variant="outline" disabled={state.loading} onClick={() => { void open(); }}><Download aria-hidden="true" className="mr-2 h-4 w-4" />Download scan</Button></div>;
};
